import {
    BaseQueryFn,
    FetchArgs,
    fetchBaseQuery,
    FetchBaseQueryError,
    FetchBaseQueryMeta,
} from '@reduxjs/toolkit/query';
import { toast } from 'react-toastify';
import { ServerError } from '@/types/common';
import { SERVER_ERRORS, ERROR_CODES } from '@/configs/codes';
import { refreshToken, clearAuthData } from '@/helpers/authRefresh';
import { getClientId } from '@/helpers/clientId';
import { logout } from '@/helpers/logout';
import { getCookie } from '@/helpers/storage';
import { STORAGE_KEYS } from '@/constants/storageKeys';

export const fetchRestApiQuery = (
    baseUrl?: string,
): BaseQueryFn<
    string | FetchArgs,
    unknown,
    FetchBaseQueryError,
    object,
    FetchBaseQueryMeta
> => {
    const baseQuery = fetchBaseQuery({
        baseUrl: `${import.meta.env.VITE_API_ENDPOINT}${baseUrl || ''}`,
        prepareHeaders: (headers) => {
            const token = getCookie('token');
            if (token) {
                headers.set('Authorization', `Bearer ${token}`);
            }
            headers.set('clientId', getClientId());
            return headers;
        },
    });

    return async (arguments_, api, extraOptions) => {
        let result = await baseQuery(arguments_, api, extraOptions);

        const url =
            typeof arguments_ === 'string' ? arguments_ : arguments_?.url || '';

        const isTelegramAuthRequest = url.startsWith('/Auth/Tg');

        const isUnauthorized =
            (result.error?.status === 'PARSING_ERROR' &&
                result.error?.originalStatus === ERROR_CODES.CODE_401) ||
            result.error?.status === ERROR_CODES.CODE_401;

        if (isUnauthorized) {
            if (isTelegramAuthRequest) {
                return result;
            }

            const refreshTokenCookie = getCookie(STORAGE_KEYS.RefreshToken);
            const userGuid = getCookie(STORAGE_KEYS.UserGuid);

            if (refreshTokenCookie && userGuid) {
                try {
                    const refreshResult = await refreshToken(
                        refreshTokenCookie,
                        userGuid,
                    );

                    if (refreshResult) {
                        result = await baseQuery(arguments_, api, extraOptions);
                    } else {
                        clearAuthData();
                        await logout(true);
                        return result;
                    }
                } catch {
                    clearAuthData();
                    await logout(true);
                    return result;
                }
            } else {
                clearAuthData();
                await logout(true);
                return result;
            }
        }

        if (
            (result.error?.status === ERROR_CODES.CODE_403 ||
                result.error?.status === ERROR_CODES.CODE_404) &&
            typeof arguments_ !== 'string' &&
            arguments_?.url?.startsWith('/Auth/RefreshToken')
        ) {
            clearAuthData();
            await logout(true);
            return result;
        }

        if (!result.meta?.response) {
            return result;
        }

        const { response } = result.meta;
        const contentType = response.headers.get('content-type');
        const contentLength = response.headers.get('content-length');
        const hasBody = contentLength && Number.parseInt(contentLength) > 0;
        const isJson = contentType?.includes('application/json');
        const isProblemJson = contentType?.includes('application/problem+json');

        if (!result.error) {
            if (
                hasBody &&
                !['application/pdf', 'image/webp'].includes(contentType || '')
            ) {
                try {
                    if (isJson || isProblemJson) {
                        const data = await response.json();

                        const url =
                            typeof arguments_ === 'string'
                                ? ''
                                : arguments_?.url;

                        if (
                            (url?.includes('Auth') ||
                                url?.includes('RefreshToken')) &&
                            (!data.token ||
                                !data.refreshToken ||
                                !data.guid ||
                                typeof data.tokenExpireIn !== 'number' ||
                                typeof data.refreshTokenExpireIn !== 'number')
                        ) {
                            toast.error(SERVER_ERRORS.er900);
                            return result;
                        }

                        return { data };
                    }
                    const text = await response.text();
                    return { data: text };
                } catch {
                    toast.error(SERVER_ERRORS.er900);
                }
            }

            return result;
        }

        const errorStatus = result.error?.status;

        if (!errorStatus) {
            return result;
        }

        if (errorStatus === ERROR_CODES.CODE_400) {
            if (!isJson && !isProblemJson) {
                return result;
            }

            try {
                const serverError: ServerError = result.error
                    .data as ServerError;
                if (!serverError?.errors) {
                    toast.error(SERVER_ERRORS.er900);
                    return result;
                }

                Object.entries(serverError.errors).forEach(
                    ([fieldName, errors]) => {
                        if (!fieldName && !errors?.length) {
                            toast.error(SERVER_ERRORS.er900);
                            return;
                        }
                        if (!fieldName) {
                            errors.forEach((error) => toast.error(error));
                        }
                    },
                );
            } catch {
                toast.error(SERVER_ERRORS.er900);
            }

            return result;
        }

        if (Number(errorStatus) >= ERROR_CODES.CODE_500) {
            toast.error(
                SERVER_ERRORS[`er${errorStatus}`] || SERVER_ERRORS.er900,
            );
            return result;
        }

        return result;
    };
};
